// BackupList.jsx
import { useState, useEffect, useCallback } from "react";
import DateRangeFilter from "../PerformancePageComponents/PeriodFilter";
import { BackupService } from "../../../../application/services/backupService";
import VmBackupCard from "./VmBackupCard";

const backupService = new BackupService();

const BackupList = ({ vmIds }) => {
    const [loading, setLoading] = useState(false);
    const [backupData, setBackupData] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const vmIdList = vmIds && Array.isArray(vmIds) ? vmIds : vmIds ? [vmIds] : [];

    const formatDateForApi = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const fetchAllBackupData = useCallback(async (start, end) => {
        if (!vmIdList.length || !start || !end) return;
        
        setLoading(true);
        try {
            const promises = vmIdList.map(vmId => 
                backupService.getVmBackupHistory(vmId, start, end)
            );
            const results = await Promise.all(promises);
            const successfulData = results
                .filter(result => result.success && result.data)
                .map(result => result.data);
            setBackupData(successfulData);
        } catch (error) {
            console.error("Error fetching backup data:", error);
        } finally {
            setLoading(false);
        }
    }, [vmIdList]);

    const handleRangeChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
        fetchAllBackupData(start, end);
    };

    useEffect(() => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        
        const defaultStart = formatDateForApi(sevenDaysAgo);
        const defaultEnd = formatDateForApi(yesterday);
        
        setStartDate(defaultStart);
        setEndDate(defaultEnd);
        fetchAllBackupData(defaultStart, defaultEnd);
    }, [fetchAllBackupData]);

    if (!vmIdList.length) {
        return (
            <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
                <div className="text-center py-6 sm:py-8">
                    <p className="text-gray-400 text-sm sm:text-base">No VM selected</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full overflow-x-hidden">
            <div className="mb-4 sm:mb-6 px-2 sm:px-0">
                <DateRangeFilter onRangeChange={handleRangeChange} />
            </div>
            
            {loading ? (
                <div className="flex justify-center items-center py-12 sm:py-20">
                    <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-400 ml-3 text-sm sm:text-base">Loading backup data...</p>
                </div>
            ) : backupData.length === 0 ? (
                <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
                    <div className="text-center py-6 sm:py-8">
                        <p className="text-gray-400 text-sm sm:text-base">No backup data available for selected period</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-6 px-2 sm:px-3">
                    {backupData.map((vm) => (
                        <VmBackupCard key={vm.vmId} vmData={vm} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BackupList;